/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/useInboxPageController.ts
 * FILE_ID: EMAIL-INBOX-CONTROLLER-001
 * PURPOSE: Compose typed inbox controller responsibilities for rendering leaves.
 */

import type { DedupedEmail } from '../../lib/dedup'
import type { InboxPageProps } from './contracts'
import { useInboxDataRuntime } from './controller/useInboxDataRuntime'
import { useInboxDerivedRuntime } from './controller/useInboxDerivedRuntime'
import { useInboxMutationActions } from './controller/useInboxMutationActions'
import { useInboxPaneRuntime } from './controller/useInboxPaneRuntime'
import { useInboxSidebarRuntime } from './controller/useInboxSidebarRuntime'
import { useInboxState } from './controller/useInboxState'

export function useInboxPageController(
  props: InboxPageProps = {},
) {
  const state = useInboxState(props)
  const data = useInboxDataRuntime(state)
  const pane = useInboxPaneRuntime(state, data)
  let currentDedupedEmails: DedupedEmail[] = []
  const mutations = useInboxMutationActions(
    state,
    data,
    () => currentDedupedEmails,
  )
  const derived = useInboxDerivedRuntime(state, data, pane)
  currentDedupedEmails = derived.dedupedEmails
  const sidebar = useInboxSidebarRuntime(state, data, derived)

  return {
    folder: state.folder,
    isInboxFolder: state.isInboxFolder,
    isOutgoingFolder: state.isOutgoingFolder,
    folderTitle: state.folderTitle,
    navigate: state.navigate,
    activeScope: state.activeScope,
    // FEAT-2026-07-26-INBOX-AI-REVIEW-FILTER — the "in AI review" strip reads
    // the flag and the toggle; the count rides on `stats.needs_review` below.
    needsReviewOnly: state.needsReviewOnly,
    toggleNeedsReviewOnly: state.toggleNeedsReviewOnly,
    isComposeMode: state.isComposeMode,
    composeKey: state.composeKey,
    kanbanApi: state.kanbanApi,
    customCategories: state.customCategories,
    kanbanSession: state.kanbanSession,
    kanbanOrgId: state.kanbanOrgId,
    searchQuery: state.searchQuery,
    setSearchQuery: state.setSearchQuery,
    smartSearchOn: state.smartSearchOn,
    toggleSmartSearch: state.toggleSmartSearch,
    searchFocused: state.searchFocused,
    setSearchFocused: state.setSearchFocused,
    searchHighlight: state.searchHighlight,
    setSearchHighlight: state.setSearchHighlight,
    debouncedSearchQuery: state.debouncedSearchQuery,
    liveSearchSuggestions: state.liveSearchSuggestions,
    parsedDebouncedSearch: state.parsedDebouncedSearch,
    selectedCategories: state.selectedCategories,
    setSelectedCategories: state.setSelectedCategories,
    toggleCategory: state.toggleCategory,
    applyImportantCategories: state.applyImportantCategories,
    allocationFilter: state.allocationFilter,
    setAllocationFilter: state.setAllocationFilter,
    setFromAddressContains: state.setFromAddressContains,
    setCompanyDomain: state.setCompanyDomain,
    rightTab: state.rightTab,
    setRightTab: state.setRightTab,
    copilotSeed: state.copilotSeed,
    aiLinkOpen: state.aiLinkOpen,
    setAiLinkOpen: state.setAiLinkOpen,
    showSidebar: state.showSidebar,
    setShowSidebar: state.setShowSidebar,
    hidePromotions: state.hidePromotions,
    setHidePromotions: state.setHidePromotions,
    hideCleared: state.hideCleared,
    setHideCleared: state.setHideCleared,
    groupByMailbox: state.groupByMailbox,
    setGroupByMailbox: state.setGroupByMailbox,
    sortMode: state.sortMode,
    setSortMode: state.setSortMode,
    shortcutsPanelOpen: state.shortcutsPanelOpen,
    setShortcutsPanelOpen: state.setShortcutsPanelOpen,
    quickTriageEmailId: state.quickTriageEmailId,
    setQuickTriageEmailId: state.setQuickTriageEmailId,
    allocateBoardEmailId: state.allocateBoardEmailId,
    setAllocateBoardEmailId: state.setAllocateBoardEmailId,
    collapsedMailboxes: state.collapsedMailboxes,
    toggleMailboxGroup: state.toggleMailboxGroup,
    selectedEmailId: state.selectedEmailId,
    setSelectedEmailId: state.setSelectedEmailId,
    closeSelectedEmail: state.closeSelectedEmail,
    selectedIds: state.selectedIds,
    setSelectedIds: state.setSelectedIds,
    bulkLinkOpen: state.bulkLinkOpen,
    setBulkLinkOpen: state.setBulkLinkOpen,
    listRef: state.listRef,
    mutedCategories: state.mutedCategories,
    emails: data.emails,
    loading: data.loading,
    pendingNewQuery: data.pendingNewQuery,
    error: data.error,
    hasMore: data.hasMore,
    loadMore: data.loadMore,
    refetch: data.refetch,
    refetchPreservingScroll: data.refetchPreservingScroll,
    updateEmailLocal: data.updateEmailLocal,
    listLoading: data.listLoading,
    syncStatus: data.syncStatus,
    triggerSync: data.triggerSync,
    handleIdentityReload: data.handleIdentityReload,
    isAutoSyncing: data.isAutoSyncing,
    stats: data.stats,
    statsError: data.statsError,
    refetchStats: data.refetchStats,
    classifyBusy: data.classifyBusy,
    toast: data.toast,
    jobActionId: data.jobActionId,
    handleConfirmJob: data.handleConfirmJob,
    handleRejectJob: data.handleRejectJob,
    allocActionId: data.allocActionId,
    handleConfirmAllocation: data.handleConfirmAllocation,
    handleRejectAllocation: data.handleRejectAllocation,
    suggestionActionId: data.suggestionActionId,
    isNarrow: pane.isNarrow,
    inboxSide: pane.inboxSide,
    readerExpanded: pane.readerExpanded,
    toggleReaderExpanded: pane.toggleReaderExpanded,
    twoPaneRef: pane.twoPaneRef,
    onSplitterPointerDown: pane.onSplitterPointerDown,
    effectiveListWidth: pane.effectiveListWidth,
    markupSrc: pane.markupSrc,
    setMarkupSrc: pane.setMarkupSrc,
    openMarkup: pane.openMarkup,
    handleClassifyUnprocessed: pane.handleClassifyUnprocessed,
    handleRecategorize: pane.handleRecategorize,
    detailBodyLoading: pane.detailBodyLoading,
    detailFetchError: pane.detailFetchError,
    refetchDetail: pane.refetchDetail,
    selectedEmail: pane.selectedEmail,
    patchSelectedEmail: pane.patchSelectedEmail,
    clearAllFilters: pane.clearAllFilters,
    handleToggleStar: mutations.handleToggleStar,
    handleEmailClick: mutations.handleEmailClick,
    handleToggleSelect: mutations.handleToggleSelect,
  handleBulkAction: mutations.handleBulkAction,
  handleEmailResolved: mutations.handleEmailResolved,
  beginTerminalRemoval: mutations.beginTerminalRemoval,
  binningId: mutations.binningId,
  handleBinRow: mutations.handleBinRow,
  archivingId: mutations.archivingId,
  handleArchiveRow: mutations.handleArchiveRow,
  unarchivingId: mutations.unarchivingId,
  handleUnarchiveRow: mutations.handleUnarchiveRow,
  restoringId: mutations.restoringId,
  handleRestoreRow: mutations.handleRestoreRow,
  /** Direct bulkAction for palette teach-loop verbs (archive/junk/complete). */
  bulkAction: data.bulkAction,
  archive: data.archive,
    readTogglingId: mutations.readTogglingId,
    handleToggleReadRow: mutations.handleToggleReadRow,
    currentUserId: derived.currentUserId,
    currentUserEmail: derived.currentUserEmail,
    hasMultipleMailboxes: derived.hasMultipleMailboxes,
    dedupedEmails: derived.dedupedEmails,
    mailboxGroupCounts: derived.mailboxGroupCounts,
    distinctMailboxCount: derived.distinctMailboxCount,
    canGroupByMailbox: derived.canGroupByMailbox,
    clearWatchdogClock: derived.clearWatchdogClock,
    loadWatchdogTripped: derived.loadWatchdogTripped,
    setLoadWatchdogTripped: derived.setLoadWatchdogTripped,
    suggestionsMap: derived.suggestionsMap,
    dismissSuggestion: derived.dismissSuggestion,
    handleConfirmSuggestion: derived.handleConfirmSuggestion,
    handleRejectSuggestion: derived.handleRejectSuggestion,
    liveSearch: derived.liveSearch,
    sidebarCategories: sidebar.sidebarCategories,
    sidebarFilters: sidebar.sidebarFilters,
    patchFilters: sidebar.patchFilters,
    applyPreset: sidebar.applyPreset,
    activeFilterCount: sidebar.activeFilterCount,
    hasActiveQuery: sidebar.hasActiveQuery,
    filterPills: sidebar.filterPills,
    removeFilterPill: sidebar.removeFilterPill,
    inviteMembers: sidebar.inviteMembers,
  }
}

export type InboxPageController = ReturnType<typeof useInboxPageController>
